import { FiBell } from "react-icons/fi";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { DropDownComp3 } from "../Ui/selectComp";
import NotificationCard from "./NotificationCard";
import {
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
} from "../../store/admin/notificationSlice";
import { useNotificationPolling, useNotificationBadge } from "../../hooks/useNotificationPolling";
import { useUser } from "../../hooks";
import { useCompanyCapabilities } from "../../hooks/useCompanyCapabilities";
import { resolveNotificationNavigationTarget } from "../../utils/notificationNavigation";
import "./notifications.css";

const DROPDOWN_LIMIT = 5;

export default function NotificationDropdown({
  guard = "staff",
  enabled = true,
  listPath = "/super_admin/notifications",
  mobile = false,
}) {
  const navigate = useNavigate();
  const user = useUser();
  const { capabilities } = useCompanyCapabilities();
  const [activeTab, setActiveTab] = useState("all");
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead, { isLoading: isMarkingAll }] = useMarkAllAsReadMutation();

  const { notifications, unreadCount, meta, isLoading, refetch } = useNotificationPolling({
    interval: 30000,
    enabled,
    queryParams: { page: 1, status: "all", limit: DROPDOWN_LIMIT },
    guard,
  });

  // Only the desktop bell owns the tab title — mobile is a second mounted instance.
  useNotificationBadge(unreadCount, { enabled: !mobile });

  const filtered = useMemo(() => {
    const sorted = [...notifications].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if (activeTab === "unread") {
      return sorted.filter((n) => !n.is_read).slice(0, 5);
    }
    return sorted.slice(0, 5);
  }, [notifications, activeTab]);

  const allCount = meta?.total ?? notifications.length;
  const unreadTabCount = unreadCount;

  const handleClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead({ notificationId: notification.id, guard });
    }
    const { path, usedFallback, reason } = resolveNotificationNavigationTarget({
      actionUrl: notification.action_url,
      guard,
      role: user?.role,
      capabilities,
      notificationId: notification.id,
      listPath,
    });

    if (usedFallback && reason === "permission_denied") {
      toast("This notification links to a page your role cannot access.", {
        icon: "ℹ️",
      });
    }

    navigate(path);
  };

  const handleMarkAll = async () => {
    if (unreadCount <= 0) return;
    await markAllAsRead(guard);
    refetch();
  };

  const panel = (
    <div className="notification-dropdown-panel">
      <div className="notification-dropdown-header">
        <h3 className="notification-dropdown-title">Notifications</h3>
        <button
          type="button"
          className="notification-dropdown-mark-all"
          onClick={handleMarkAll}
          disabled={unreadCount <= 0 || isMarkingAll}
        >
          Mark all as read
        </button>
      </div>

      <div className="notification-dropdown-tabs">
        <button
          type="button"
          className={`notification-dropdown-tab ${activeTab === "all" ? "is-active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          All
          <span className="notification-tab-badge">{allCount > 99 ? "99+" : allCount}</span>
        </button>
        <button
          type="button"
          className={`notification-dropdown-tab ${activeTab === "unread" ? "is-active" : ""}`}
          onClick={() => setActiveTab("unread")}
        >
          Unread
          <span className="notification-tab-badge">
            {unreadTabCount > 99 ? "99+" : unreadTabCount}
          </span>
        </button>
      </div>

      <div className="notification-dropdown-list">
        {isLoading ? (
          <div className="notification-empty">
            <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            <p className="mt-2 mb-0 small">Loading notifications…</p>
          </div>
        ) : filtered.length > 0 ? (
          filtered.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onClick={handleClick}
              compact
            />
          ))
        ) : (
          <div className="notification-empty">
            <div className="notification-empty__icon" aria-hidden>
              <FiBell size={28} />
            </div>
            <p className="mb-0 fw-semibold">
              {activeTab === "unread" ? "You're all caught up" : "No notifications yet"}
            </p>
            <p className="small text-muted mt-1 mb-0">
              {activeTab === "unread"
                ? "No unread notifications right now."
                : "New activity will appear here."}
            </p>
          </div>
        )}
      </div>

      <div className="notification-dropdown-footer">
        <Link to={listPath} className="notification-dropdown-view-all">
          View all notifications
        </Link>
      </div>
    </div>
  );

  const bell = (
    <div className="position-relative d-inline-flex align-items-center">
      <img src="/Images/TopHeader/notification.svg" alt="Notifications" width={22} height={22} />
      {unreadCount > 0 && (
        <span
          className="position-absolute top-0 start-100 translate-middle badge rounded-pill notification-count-badge"
          style={{ fontSize: "10px", minWidth: "18px", height: "18px" }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </div>
  );

  return (
    <DropDownComp3
      dropLabelClassName="border-0"
      Transform={mobile ? "translate3d(-88px, 55px, -5px)" : "translate3d(-168px, 55px, -5px)"}
      DropDownText={bell}
      drop="down"
      bodyComp={panel}
    />
  );
}
